import { useState, useEffect, useRef } from "preact/hooks";
import BrutalModal from "./BrutalModal";
import Avatar from "./Avatar";
import AvatarPicker from "./AvatarPicker";
import { putEvent } from "../api";
import type { AvatarBitmap } from "../avatars";

const FACE_SIZE = 64;

export type ThemeValue = "light" | "dark";

/** PWA install prompt (Chromium). */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
  slug: string;
  username: string;
  avatar?: AvatarBitmap | null;
  notificationsEnabled: boolean;
  /** False when server has no VAPID keys (push not configured); checkbox is disabled. */
  notificationsSupported: boolean;
  onEnableNotifications: () => Promise<void>;
  onDisableNotifications: () => Promise<void>;
  onNotificationsChange: (enabled: boolean) => void;
  onAccept?: (opts: { username?: string; notifications?: boolean; theme?: ThemeValue }) => void;
  installPrompt?: InstallPromptEvent | null;
  isStandalone?: boolean;
}

function getCurrentTheme(): ThemeValue {
  if (typeof document === "undefined") return "light";
  try {
    return document.documentElement.classList.contains("dark") ? "dark" : "light";
  } catch {
    return "light";
  }
}

export default function SettingsDialog({
  open,
  onClose,
  slug,
  username,
  avatar,
  notificationsEnabled,
  notificationsSupported,
  onEnableNotifications,
  onDisableNotifications,
  onNotificationsChange,
  onAccept,
  installPrompt = null,
  isStandalone = false,
}: SettingsDialogProps) {
  const [nameValue, setNameValue] = useState(username);
  const [notificationsChecked, setNotificationsChecked] = useState(notificationsEnabled);
  const [themeValue, setThemeValue] = useState<ThemeValue>(getCurrentTheme());
  const [installing, setInstalling] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const themeOnOpenRef = useRef<ThemeValue>(getCurrentTheme());

  const handleInstallClick = async () => {
    if (!installPrompt) return;
    setInstalling(true);
    try {
      await installPrompt.prompt();
    } finally {
      setInstalling(false);
    }
  };

  useEffect(() => {
    if (open) {
      setNameValue(username);
      setNotificationsChecked(notificationsEnabled);
      const current = getCurrentTheme();
      setThemeValue(current);
      themeOnOpenRef.current = current;
    }
  }, [open, username, notificationsEnabled]);

  const handleThemeChange = (next: ThemeValue) => {
    setThemeValue(next);
    window.meetdaySetTheme?.(next);
  };

  const handleAccept = async () => {
    const updates: { username?: string; notifications?: boolean; theme?: ThemeValue } = {};

    if (nameValue.trim() !== username) {
      const newName = nameValue.trim();
      if (newName) {
        try {
          const res = await putEvent(slug, {
            type: "username_changed",
            message: "",
            data: { name: newName },
          });
          if (!res.ok) throw new Error("Failed to update name");
          updates.username = newName;
        } catch {
          // Leave dialog open; user can retry or cancel.
          return;
        }
      }
    }

    if (notificationsChecked !== notificationsEnabled) {
      if (notificationsChecked && !notificationsSupported) {
        // Checkbox is disabled when not supported; skip so we don't call onEnableNotifications (which would throw).
      } else {
        if (notificationsChecked) {
          await onEnableNotifications();
        } else {
          await onDisableNotifications();
        }
        onNotificationsChange(notificationsChecked);
        updates.notifications = notificationsChecked;
      }
    }

    updates.theme = themeValue;

    onAccept?.(updates);
    onClose();
  };

  const handleCancel = () => {
    setNameValue(username);
    setNotificationsChecked(notificationsEnabled);
    setThemeValue(themeOnOpenRef.current);
    window.meetdaySetTheme?.(themeOnOpenRef.current);
    onClose();
  };

  if (!open) return null;

  return (
    <BrutalModal
      open={open}
      onClose={handleCancel}
      title="Settings"
      maxWidth="max-w-sm"
      titleClassName="text-2xl font-logo font-bold tracking-tight uppercase text-gray-900 dark:text-white"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-800 dark:text-gray-100">
            Name
          </label>
          <input
            type="text"
            value={nameValue}
            onInput={(e) => setNameValue((e.target as HTMLInputElement).value)}
            maxLength={120}
            placeholder="Your name"
            className="brutal-input px-4 py-2.5 text-base rounded-none border-2 border-black dark:border-gray-100 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 w-full"
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">
            Image
          </span>
          <AvatarPicker
            slug={slug}
            username={username}
            trigger={(_openModal, openWithFile) => (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file) openWithFile(file);
                    (e.target as HTMLInputElement).value = "";
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center justify-center rounded-none border-2 border-black dark:border-gray-100 bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 p-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brutal-orange w-16 h-16 flex-shrink-0"
                  title="Change face"
                  aria-label="Change face"
                >
                  <Avatar username={username} avatar={avatar} size={FACE_SIZE} />
                </button>
              </>
            )}
          />
        </div>

        <div className="flex flex-col gap-1 p-3 border-2 border-black dark:border-gray-100 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="settings-notifications"
              checked={notificationsChecked}
              disabled={!notificationsSupported}
              onChange={(e) => setNotificationsChecked((e.target as HTMLInputElement).checked)}
              className="rounded-none border-2 border-black dark:border-gray-100 w-5 h-5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <label
              htmlFor="settings-notifications"
              className={`font-logo font-bold text-sm uppercase tracking-tight text-gray-900 dark:text-gray-100 select-none ${notificationsSupported ? "cursor-pointer" : "cursor-not-allowed opacity-70"}`}
            >
              Enable notifications
            </label>
          </div>
          {!notificationsSupported && (
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Not available: server is not configured for push (set MEETDAY_VAPID_PUBLIC and MEETDAY_VAPID_PRIVATE).
            </p>
          )}
        </div>

        {!isStandalone ? (
          <div className="flex flex-col gap-2 p-3 border-2 border-black dark:border-gray-100 bg-white dark:bg-gray-900">
            <span className="font-logo font-bold text-xs uppercase tracking-tight text-gray-800 dark:text-gray-100">
              Install app
            </span>
            {installPrompt ? (
              <button
                type="button"
                onClick={handleInstallClick}
                disabled={installing}
                className="brutal-btn inline-flex justify-center items-center min-h-[2.5rem] px-4 py-2.5 font-semibold rounded-none border-2 border-black dark:border-gray-100 w-full text-sm bg-brutal-purple text-white hover:bg-brutal-purple-hover disabled:opacity-60"
              >
                {installing ? "…" : "Install Meetday"}
              </button>
            ) : (
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Use your browser menu: <strong>⋮</strong> → Install Meetday (Chrome/Edge). On iPhone/iPad: <strong>Share</strong> → Add to Home Screen.
              </p>
            )}
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          <span className="font-logo font-bold text-xs uppercase tracking-tight text-gray-800 dark:text-gray-100">
            Theme
          </span>
          <div className="flex gap-0 border-2 border-black dark:border-gray-100 overflow-hidden">
            <label className="flex-1 cursor-pointer">
              <input type="radio" name="theme" checked={themeValue === "light"} onChange={() => handleThemeChange("light")} className="sr-only" />
              <span
                className={`block text-center py-2.5 px-4 font-logo font-bold text-sm uppercase tracking-tight border-r-2 border-black dark:border-gray-100 transition-colors ${
                  themeValue === "light"
                    ? "bg-brutal-orange text-black dark:text-gray-900"
                    : "bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
              >
                Light
              </span>
            </label>
            <label className="flex-1 cursor-pointer">
              <input type="radio" name="theme" checked={themeValue === "dark"} onChange={() => handleThemeChange("dark")} className="sr-only" />
              <span
                className={`block text-center py-2.5 px-4 font-logo font-bold text-sm uppercase tracking-tight ${
                  themeValue === "dark"
                    ? "bg-brutal-orange text-black dark:text-gray-900"
                    : "bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
              >
                Dark
              </span>
            </label>
          </div>
        </div>

        <form method="post" action={`/c/${slug}/logout`} className="m-0">
          <button
            type="submit"
            className="brutal-btn inline-flex justify-center min-h-[2.5rem] px-5 py-2.5 font-semibold rounded-none border-2 border-black dark:border-gray-100 w-full text-sm bg-transparent text-gray-900 dark:text-gray-100 hover:bg-orange-50 dark:hover:bg-gray-800"
          >
            Log out
          </button>
        </form>

        <div className="flex gap-2 justify-end pt-2 border-t-2 border-black dark:border-gray-100">
          <button
            type="button"
            onClick={handleCancel}
            className="brutal-btn inline-flex justify-center min-h-[2.25rem] px-4 py-1.5 text-sm font-semibold rounded-none border-2 border-black dark:border-gray-100 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 hover:bg-orange-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAccept}
            className="brutal-btn inline-flex justify-center min-h-[2.25rem] px-4 py-1.5 text-sm font-semibold rounded-none border-2 border-black dark:border-gray-100 bg-brutal-orange text-black dark:text-gray-900 hover:bg-brutal-orange-hover"
          >
            Accept
          </button>
        </div>
      </div>
    </BrutalModal>
  );
}
